import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTenantNavigation } from "../../hooks/useTenantNavigation.js";
import { Button, Tooltip, Typography, Form, message, Dropdown, Modal, Input, Checkbox, Steps, Popover } from "antd";
import Sharings from "./Sharings";
import General from "./General";
import Rules from "./Rules";
import AdvancedSettings from "./advancedSettings"
import {
  useParams,
  useSearchParams,
} from "react-router-dom";
import axios from "../../axios-interceptor.js";
import { trimStringValues } from "../../utils/trimStrings";
import { readMasterRestrictions } from "../../utils/masterRestrictions";
import { DownloadOutlined, HomeOutlined, QuestionCircleOutlined, CheckCircleFilled, MoreOutlined, SearchOutlined, CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import BreadCrumb from "../BreadCrumb";
import { createSpace, deleteSpace, getSpaceDetails, copySpace } from "../../services/SpaceServices";
import { getUserSpaceEntitlements } from "../../utils/currentUserDetail";
import { fetchSpaceImages, fetchSpaceUsers, fetchUserInfo } from "../../redux/actions/userAction";
import { showLoader } from "../../utils/showloader";
import { getMasterList, getRoles, getSingleMasterData } from "../../services/adminServices";
import exportExcel from "../../utils/exportExcel";

const layout = {
  labelCol: { span: 5 },
  wrapperCol: { span: 10 },
};

const options = [
  { label: "Space Settings", value: "copySpaceSettings" },
  { label: "Space Members", value: "copySpaceMembers" },
  { label: "Workflow", value: "copyWorkflow" },
  { label: "Workflow Conditions", value: "copyWorkflowConditions" },
  { label: "Forms", value: "copyForms" },
  { label: "Form Conditions", value: "copyFormConditions" },
  { label: "Notification", value: "copyNotification" },
];

const BACKEND_API = process.env.REACT_APP_BACKEND_API;
let tenantId = localStorage.getItem("tenantId");

// Add responsive styles for better mobile experience
const addResponsiveStyles = () => {
  if (typeof document !== 'undefined' && !document.head.querySelector('style[data-responsive-space]')) {
    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-responsive-space', 'true');
    styleElement.textContent = `
      @media (max-width: 640px) {
        .page-wrapper { padding: 0; }
        .copy-space-modal .ant-modal-content { margin: 16px; }
        .widget-tab { padding: 8px 16px; text-align: center; border-radius: 6px; transition: all 0.2s ease; }
        .ant-steps-item-title { font-size: 14px !important; }
        .ant-form-item-label { padding-bottom: 4px; }
      }
      @media (max-width: 480px) {
        .ant-typography-title { font-size: 18px !important; }
        .ant-btn { height: 40px; }
      }
    `;
    document.head.appendChild(styleElement);
  }
};

const SpaceSettingsPage = () => {
  const { t, i18n } = useTranslation();
  const [spaceData, setSpaceData] = useState({});
  const { spaceId } = useParams();
  const navigate = useTenantNavigation();
  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("general");
  const [submited, setSubmitted] = useState(false);
  const [submitable, setSubmitable] = useState(false);
  const [download, setDownload] = useState(false)
  const [roles, setRoles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reloadSpaceData, setReloadSpaceData] = useState(0);
  const [form2] = Form.useForm();
  const [form4] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [optionsToBeCopied, setOptionsToBeCopied] = useState([]);
  const [copySpaceFormData, setCopySpaceFormData] = useState({});
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [congrates, setCongrates] = useState(false);
  const [createdSpaceId, setCreatedSpaceId] = useState("");
  const [masterList, setMasterList] = useState([]);
  const [rulesData, setRulesData] = useState({
    "archive": { enable: false, count: '1', period: "month", occurrence: "after", event: "updatedOn" },
    "delete": { enable: false, count: '1', period: "month", occurrence: "after", event: "updatedOn" }
  });
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  let { userInfo } = useSelector((state) => state.user);
  let path = "/app/dashboard";
  let spaceEntitlements = [];

  // Add responsive styles
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    addResponsiveStyles();
  }, []);

  useEffect(() => {
    if (spaceData.rules) {
      let ruleObj = { ...rulesData }
      spaceData.rules.map(rule => {
        ruleObj[rule.ruleType] = { enable: true, count: rule.count, period: rule.period, occurrence: rule.occurrence, event: rule.event }
      })
      setRulesData(ruleObj)
    }
  }, [spaceData])

  if (userInfo) {
    if (userInfo.userAccess) {
      spaceEntitlements = getUserSpaceEntitlements(spaceId, userInfo.userAccess)

    }
    if (userInfo.isAdmin) {
      path = "/app/dashboard"
    }
  }

  const fetchSpaceData = async () => {
    showLoader(true)
    let spaceDetails = await getSpaceDetails(spaceId);
    showLoader(false)

    if (spaceDetails.status === 200) {
      if (userInfo.defaultLanguage) {
        i18n.changeLanguage(userInfo.defaultLanguage)
      } else if (spaceDetails.data.hasOwnProperty("defaultLanguage") && spaceDetails.data.defaultLanguage) {
        i18n.changeLanguage(spaceDetails.defaultLanguage)
      }
      let spaceDetailsData = spaceDetails?.data || {};
      const restrictions = readMasterRestrictions(spaceDetailsData)
      spaceDetailsData = { ...spaceDetailsData, masterToRestrictAccess: restrictions }
      setSpaceData(spaceDetailsData)
      // The form splits the restrictions into the ids it multi-selects and a
      // dataId -> column map, one entry per column picker.
      form.setFieldsValue({
        ...spaceDetailsData,
        masterToRestrictAccess: restrictions.map(r => r.dataId),
        masterColumns: restrictions.reduce((acc, r) => ({ ...acc, [r.dataId]: r.column }), {}),
      })
    } else {
      message.error(spaceDetails.message)
    }
  }

  const fetchMasterData = useCallback(async () => {
      showLoader(true);
      const resp = await getMasterList();
      showLoader(false);
      if (resp?.data) {
        let masterData = resp.data.map(data => ({label: data.dataName, value: data.dataId}))
        setMasterList(masterData);
      } else {
        setMasterList([]);
      }
    }, []);

  useEffect(() => {
    if (reloadSpaceData > 0) {
      dispatch(fetchUserInfo())
    }
  }, [reloadSpaceData])

  useEffect(() => {
    if (spaceId) {
      fetchSpaceData();
      dispatch(fetchSpaceUsers())
      // fetchMasterData();
    }
  }, [])

  const fetchRoles = useCallback(async () => {
    let userRoles = await getRoles();
    setRoles(userRoles)
  }, [])

  const fetchCategories = useCallback(async () => {
    let categoryResponse = await getSingleMasterData("", 'Category');
    if (categoryResponse?.data?.data?.length) {
      let categoryData = categoryResponse.data.data;
      categoryData = categoryData.map(data => {
        return { label: data["Name"], value: data["Name"] }
      })
      setCategories(categoryData)
    }
    // setRoles(userRoles)
  }, [])

  const fetchMasterList = useCallback(async () => {
    let masterResponse = await getMasterList();
    if (masterResponse?.data) {
      // let data = masterResponse?.data?.map(master => )
      setMasterList(masterResponse.data);
    } else {
      setMasterList([]);
    }
  }, [])

  useEffect(() => {
    fetchRoles();
    fetchCategories();
    fetchMasterList();
  }, [fetchRoles, fetchCategories]);

  const reloadSpace = () => {
    setReloadSpaceData(reloadSpaceData + 1)
  }

  const handleSaveButton = (state) => {
    setSubmitable(state);
    setSubmitted(false)
  }

  const onChangeTab = (value) => {
    setSearchParams({ tab: value });
    setActiveTab(value);
    setSubmitted(false)
    setSubmitable(false)
  };

  const handleSpaceDelete = async () => {
    showLoader(true)
    let deleteResponse = await deleteSpace(spaceId);
    if (deleteResponse.status !== 200) {
      message.error(deleteResponse.message)
      return
    }
    message.success(deleteResponse.message)
    dispatch(fetchUserInfo());
    navigate("/app/dashboard")

    showLoader(false)
  }

  const handleSpaceChange = (key, val) => {
    setSpaceData({ ...spaceData, [key]: val })
  }

  const handleCopyFormSubmit = (values) => {
    // form2.resetFields();
    setCopySpaceFormData({ ...values })
    setCurrentStep(currentStep + 1)
  };

  const submitSpaceCategory = async () => {
    let postData = { spaceId: spaceId, name: copySpaceFormData.name, description: copySpaceFormData.description, abbreviation: copySpaceFormData.abbreviation.toUpperCase() }
    options.forEach(opt => {
      let copy = false;
      if (optionsToBeCopied.includes(opt.value)) {
        copy = true
      }
      postData[opt.value] = copy
    })
    // return
    showLoader(true)
    await axios({
      method: "POST",
      url: `${BACKEND_API}/space-copy`,
      data: postData,
      headers: {
        userId: userInfo.userId,
        tenantId: tenantId
      },
    }).then(async (response) => {
      let data = response.data
      if (data?.status === 200) {
        message.success(t('Space copied successfully'))
        dispatch(fetchUserInfo())
        setCreatedSpaceId(data?.result?.spaceId)
        setCongrates(true)
      } else {
        message.error(t(data))
      }
    })
      .catch((error) => {
      });
    showLoader(false)
  }

  const navigateToSpace = () => {
    setShowCopyModal(false)
    navigate(`/app/space/${createdSpaceId}/space-settings?type=general`)
  }

  const saveSpace = async (values) => {
    values = trimStringValues(values);
    let postRuleData = []
    Object.keys(rulesData).forEach(key => {
      if (rulesData[key].enable) {
        let obj = { ruleType: key, count: Number(rulesData[key].count), period: rulesData[key].period, occurrence: rulesData[key].occurrence, event: rulesData[key].event }
        postRuleData.push(obj)
      }
    })
    const selectedMasterIds = Array.isArray(values.masterToRestrictAccess)
      ? values.masterToRestrictAccess
      : (values.masterToRestrictAccess ? [values.masterToRestrictAccess] : []);
    const masterColumns = values.masterColumns || {};
    const restrictions = selectedMasterIds
      .filter(Boolean)
      .map(dataId => ({ dataId, column: masterColumns[dataId] }));
    let postData = {
      ...spaceData, ...values,
      rules: postRuleData,
      allowedDuplicate: values?.allowedDuplicate?.toString() || false,
      enableChatroom: values?.enableChatroom?.toString() || false,
      olderVersion: values?.olderVersion?.toString() || false,
      spaceIcon: spaceData.spaceIcon,
      spaceColor: spaceData.spaceColor,
      // The form holds the ids and their columns apart; they go back out as the
      // single list the space is saved with.
      masterToRestrictAccess: restrictions
    }
    // Form-only scratch field, and the single-value column both spaceData and
    // values can still carry from an older save.
    delete postData.masterColumns
    delete postData.masterColumnToRestrict
    // return
    showLoader(true)
    let updateResponse = await createSpace(postData, userInfo)
    showLoader(false)
    if (updateResponse.status !== 200) {
      message.error(updateResponse.message)
      return
    }
    message.success(t('Space settings updated.'));
    // A backend that still echoes back the old single-value string would drop
    // every restriction past the first, so what was just posted wins unless the
    // response carries the list itself.
    const savedSpace = { ...updateResponse.data }
    setSpaceData({
      ...savedSpace,
      masterToRestrictAccess: Array.isArray(savedSpace.masterToRestrictAccess)
        ? readMasterRestrictions(savedSpace)
        : restrictions
    })
    dispatch(fetchUserInfo())
  };

  const handleSave = () => {
    form.submit();
  }

  const handleCancel = () => {
    setShowCopyModal(false)
    setCongrates(false);
    form2.resetFields();
    form4.resetFields();
    setCurrentStep(0)
  };

  const confirmSpaceCopy = () => {
    form2.setFieldsValue({ name: spaceData.name + " copy" })
    setShowCopyModal(true)
  }

  const confirmSpaceDelete = () => {
    Modal.confirm({
      title: t('Confirm'),
      content: t('Are you sure to delete the space? \n Once deleted, you will not be able to access the space and its content.'),
      okText: t("Yes"),
      cancelText: t("No"),
      onOk: (event) => {
        handleSpaceDelete()
        Modal.destroyAll()
      },
      footer: (_, { OkBtn, CancelBtn }) => (
        <>
          <CancelBtn />
          <OkBtn />
        </>
      ),
    });
  }
  const spaceActions = [
    {
      key: "copy",
      label: <div className="flex items-center gap-2 w-30"><CopyOutlined /><span>{t("Copy")}</span></div>,
      onClick: confirmSpaceCopy
    },
    {
      key: "delete",
      label: <div className="flex items-center gap-2 w-30"><DeleteOutlined /><span>{t("Delete")}</span></div>,
      danger: true,
      onClick: confirmSpaceDelete
    },
  ];

  const handleSpaceCopyCategory = (val) => {
    let reqVal = [...val]
    if (reqVal.includes("copyFormConditions")) {
      if (!reqVal.includes("copyForms")) {
        reqVal.push("copyForms")
      }
      if (!reqVal.includes("copyWorkflow")) {
        reqVal.push("copyWorkflow")
      }
    }
    if ((reqVal.includes("copyWorkflowConditions") || reqVal.includes("copyForms")) && !reqVal.includes("copyWorkflow")) {
      reqVal.push("copyWorkflow")
    }
    form4.setFieldsValue({ optionsToBeCopied: reqVal })
    setOptionsToBeCopied(reqVal)
  }

  const getActions = (tab) => {
    if (tab === "general") {
      return (
        <div className="flex gap-2">
          <Button
            size="middle"
            className="success-button"
            loading={submited}

            onClick={() => handleSave()}
          >
            {t("Save Changes")}
          </Button>
          <Dropdown
            overlayClassName="user-account"
            trigger={"click"}
            menu={{ items: spaceActions }}
            placement="bottomRight"
            arrow
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        </div>
      );
    } else if (tab === "members") {
      return (
        <div className="flex gap-2">
          {/* <Button icon={<SearchOutlined />} /> */}
          <Tooltip title={t("Download")}>
            <Button icon={<DownloadOutlined />} onClick={() => setDownload(true)}>
              {t('Download')}
            </Button>
          </Tooltip>
        </div>
      );
    }
    return null;
  };
  const breadCrumbItems = [
    {
      title: <HomeOutlined />,
      path: path,
    },
    {
      title: spaceData.name,
      path: `/app/space/${spaceId}/documents`
    },
    {
      title: t("Space Settings"),
    },
  ];

  const handleDownload = (data) => {
    let dataToDownload = data.map(user => ({ name: user.name, email: user.memberCount ? `${user.memberCount} members` : user.email, role: user.role }))
    let fileName = "Export " + spaceData.name + " - Members"
    exportExcel([{ title: t('User/Group'), dataIndex: 'name' }, { title: t('Email'), dataIndex: 'email' }, { title: t('Role'), dataIndex: 'role' }], dataToDownload, fileName)
    setDownload(false)
  }

  return (
    <div className=" min-h-screen bg-gray-50" key={i18n.language}>
      {/* Header */}
      <div className="sticky-space-header">
        {/* Breadcrumb */}
        <div className="mb-2">
          <BreadCrumb items={breadCrumbItems} marginb="16px" />
        </div>
        <div className="mb-2">
          <div className="flex flex-wrap sm:items-center sm:justify-between gap-4">
            <Typography.Title level={4} className="!m-0 text-gray-900 items-center justify-center">
              {t("Space Setup")}
            </Typography.Title>
            <div className="flex justify-end">
              {getActions(activeTab)}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-2">
          <div className="bg-gray-100 rounded-lg p-1 inline-flex w-full sm:w-auto">
            <button
              className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === "general"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              onClick={() => onChangeTab("general")}
            >
              {t("General")}
            </button>
            <button
              className={`flex-1 sm:flex-none px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${activeTab === "members"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                }`}
              onClick={() => onChangeTab("members")}
            >
              {t("Members")}
            </button>
          </div>
        </div>

      </div>
      <div className="px-4 sm:px-6 lg:px-8 pb-2 mx-auto">

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6">
          {activeTab === "general" ? (
            <Form {...layout} labelAlign="left" name="space-settings-form" form={form} onFinish={saveSpace} 
            // initialValues={{
            //   ...spaceData
            // }}
             layout="vertical">
              <div className="space-y-6">
                <General
                  handleSpaceChange={handleSpaceChange}
                  submitable={submitable}
                  submited={activeTab != "general" ? false : submited}
                  onSubmit={() => setSubmitable(false)}
                  spaceData={spaceData}
                  onChange={handleSaveButton}
                  reloadSpace={reloadSpace}
                  userInfo={userInfo}
                  spaceEntitlements={spaceEntitlements}
                  categories={categories}
                  masterList={masterList}
                  formValues={formValues}
                  form={form}
                />
                <AdvancedSettings
                  submited={activeTab != "advanced" ? false : submited}
                  onSubmit={() => setSubmitable(false)}
                  onChange={() => { setSubmitable(true); setSubmitted(false) }}
                  userInfo={userInfo}
                  spaceData={spaceData}
                  spaceEntitlements={spaceEntitlements}
                />
                <Rules
                  rulesData={rulesData}
                  setRulesData={setRulesData}
                  submited={activeTab != "rules" ? false : submited}
                  onSubmit={() => setSubmitted(false)}
                  onChange={() => { setSubmitable(true); setSubmitted(false) }}
                  spaceEntitlements={spaceEntitlements}
                />
              </div>
            </Form>
          ) : (
            <Sharings
              spaceData={spaceData}
              roles={roles}
              masterList={masterList}
              submited={activeTab != "sharings" ? false : submited}
              onSubmit={() => setSubmitable(false)}
              onChange={() => { setSubmitable(true); setSubmitted(false) }}
              userInfo={userInfo}
              spaceEntitlements={spaceEntitlements}
              download={download}
              handleDownload={handleDownload}
            />
          )}
        </div>
      </div>

      {/* Copy Space Modal */}
      <Modal
        title={congrates ? "" : t("Copy space")}
        open={showCopyModal}
        onCancel={handleCancel}
        width={600}
        footer={null}
      >
        {congrates ? (
          <div className="text-center py-8">
            <CheckCircleFilled className="text-green-500 text-6xl mb-4" />
            <Typography.Title level={4} className="!mb-2">{t('Congratulations!')}</Typography.Title>
            <Typography.Paragraph className="text-gray-500 mb-6">
              {t("Space copied successfully.")}
            </Typography.Paragraph>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button type="primary" onClick={navigateToSpace} className="w-full sm:w-auto">
                {t("Take me to space")}
              </Button>
              <Button type="default" onClick={handleCancel} className="w-full sm:w-auto">
                {t("Close")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="py-4">
            <Steps
              progressDot
              current={currentStep}
              className="mb-8"
              items={[
                { title: t("General Details") },
                { title: t("Copy Details") }
              ]}
            />
            {currentStep === 0 ? (
              <Form form={form2} layout="vertical" onFinish={handleCopyFormSubmit} className="w-full">
                <Form.Item
                  className="mb-4"
                  label={t("Space Name")}
                  name="name"
                  rules={[{ required: true, whitespace: true, message: t("Space name is required") }]}
                >
                  <Input placeholder={t("Enter Space Name")} />
                </Form.Item>

                <Form.Item
                  className="mb-4"
                  label={
                    <div className="flex items-center gap-2">
                      {t("Space Abbreviation")}
                      <Popover
                        content={<div className="max-w-xs p-2">{t("This cannot be changed in the future.")}</div>}
                        trigger="hover"
                      >
                        <QuestionCircleOutlined className="text-gray-400" />
                      </Popover>
                    </div>
                  }
                  name="abbreviation"
                  rules={[
                    { required: true, whitespace: true, message: t("Space abbreviation is required") },
                    { max: 6, message: t("Maximum 6 characters are allowed") },
                    { min: 2, message: t("Minimum 2 characters are allowed") }
                  ]}
                >
                  <Input placeholder={t("Enter space abbreviation")} />
                </Form.Item>

                <Form.Item label={t("Space Description")} name="description" className="mb-6">
                  <Input.TextArea rows={3} placeholder={t("Enter space description")} />
                </Form.Item>

                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <Button type="default" onClick={handleCancel} className="w-full sm:w-auto">
                    {t("Cancel")}
                  </Button>
                  <Button type="primary" htmlType="submit" className="w-full sm:w-auto">
                    {t("Next")}
                  </Button>
                </div>
              </Form>
            ) : (
              <Form form={form4} onFinish={submitSpaceCategory} layout="vertical" className="w-full">
                <Form.Item
                  name="optionsToBeCopied"
                  label={t("Select what needs to be copied in new space")}
                  rules={[{ required: true, message: "Select items to be copied" }]}
                  className="mb-6"
                >
                  <Checkbox.Group
                    onChange={handleSpaceCopyCategory}
                    options={options}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                  />
                </Form.Item>

                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <Button type="default" onClick={() => setCurrentStep(currentStep - 1)} className="w-full sm:w-auto">
                    {t("Back")}
                  </Button>
                  <Button type="primary" htmlType="submit" className="w-full sm:w-auto">
                    {t("Copy space")}
                  </Button>
                </div>
              </Form>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SpaceSettingsPage;
